'use client';

import {
  Children,
  cloneElement,
  createContext,
  createElement,
  forwardRef,
  Fragment,
  isValidElement,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import {
  useModalOpenPayload,
  type ModalOpenPayload,
} from '../composites/Modal/ModalHost';
import { COMPONENT_IDS } from './component-ids';

const CATALOG_ID_TO_EXPORT_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(COMPONENT_IDS).map(([exportName, id]) => [id, exportName]),
);

/**
 * Puck's slot transform yields a function that accepts `{ as }` and returns
 * a DropZone / SlotRender element. Editor DropZone components are hooky and
 * must be mounted; the transform Slot itself is safe to invoke as a render
 * prop (DslConfig `StableSlot` pattern).
 */
export type PuckSlotAs = ComponentType<{ children?: ReactNode }>;

export type PuckSlotRenderFn = (props: { as?: PuckSlotAs }) => ReactNode;

export type PuckSlotFn = ComponentType<{ as?: PuckSlotAs }> | PuckSlotRenderFn;

/**
 * A resolved component that reads its slot props as raw `{ type, props }`
 * JSON itself. Slot reconstruction hands it the node props unchanged.
 */
export type SlotHostComponent = ComponentType<Record<string, unknown>> & {
  ownsSlotProps?: true;
};

export type ResolveSlotComponent = (type: string) => SlotHostComponent | null;

/**
 * Export names whose CSF sets `parameters.edu.resolveSlotChildren`.
 * Runtime renderers that do not load the catalog JSON (DslConfig) read this
 * set; `createPuckConfig` honors the catalog field. Keep in sync with stories
 * that set `resolveSlotChildren` (ShowMore, StatColumns, Tabs). Reconstruction of the
 * empty-content editor path also needs each child in
 * {@link SLOT_COLLECTION_CHILD_EXPORT_NAMES}. Do not special-case those
 * components inside their modules.
 */
export const RESOLVE_SLOT_CHILDREN_EXPORT_NAMES: ReadonlySet<string> = new Set([
  'ShowMore',
  'StatColumns',
  'Tabs',
]);

export const shouldResolveSlotChildren = (
  exportName: string,
  catalogFlag?: boolean,
): boolean => {
  if (catalogFlag === true) return true;
  if (catalogFlag === false) return false;
  return RESOLVE_SLOT_CHILDREN_EXPORT_NAMES.has(exportName);
};

/**
 * Export names that render as Puck `inline` components on the editor canvas.
 * Puck then skips its item wrapper `div` and the adapter puts Puck's drag ref
 * on the component's own element, so flex and grid layouts size the component
 * the way Preview does, and table parts keep valid table markup. Every name
 * here must always render one root element and forward `ref` to it, or take
 * an editor root ref (see {@link EDITOR_ROOT_REF_EXPORT_NAMES}). Puck forces
 * `position: relative` on that element while editing.
 */
export const INLINE_COMPONENT_EXPORT_NAMES: ReadonlySet<string> = new Set([
  'Accordion',
  'AccordionItem',
  'ArticleCard',
  'ArticleHeroBanner',
  'ArticleLayout',
  'ArticleRow',
  'Avatar',
  'AvatarGroup',
  'Badge',
  'Bar',
  'BarChart',
  'BentoBox',
  'BrandCard',
  'Brow',
  'Button',
  'Callout',
  'Card',
  'CardContent',
  'CardFooter',
  'Carousel',
  'CarouselItem',
  'ChartFrame',
  'ChatMessage',
  'ChatMessageAvatar',
  'ChatMessageContent',
  'CircularProgress',
  'ComboChart',
  'DataPoint',
  'DegreeCardDetailed',
  'DegreeCardSimple',
  'DegreeCardTiny',
  'EditorialListing',
  'ExpertBox',
  'ExpertQuote',
  'FileDropzone',
  'FileDropzoneHint',
  'FileDropzonePrompt',
  'Flex',
  'FormField',
  'Grid',
  'Image',
  'InsightCard',
  'KPIBanner',
  'Label',
  'LineChart',
  'LinearGauge',
  'Link',
  'ListItem',
  'LogoBox',
  'NavigationWrapper',
  'PillButton',
  'Price',
  'ProgressGroup',
  'PropertyValue',
  'Radio',
  'RankingCard',
  'ScatterChart',
  'Section',
  'SectionHeader',
  'SelectionTile',
  'Separator',
  'Split',
  'SponsoredCard',
  'StatStyles',
  // Sticky lives on an inner column. The ref element is the outer row, so
  // Puck's position: relative does not undo the column's sticky rule.
  'StickyColumn',
  'StudentLoansBanner',
  'StudentLoansInline',
  'TableBody',
  'TableCell',
  'TableHead',
  'TableHeader',
  'TableRow',
  'TagLink',
  'Text',
  'Timeline',
  'TimelineEvent',
  'TimelineEventDescription',
  'TocNav',
  'ToggleGroup',
  'ToggleGroupItem',
  'WaterfallChart',
]);

/**
 * Inline components whose public `ref` points at an inner element on
 * purpose, such as Image's photo. The adapter hands them Puck's drag ref as
 * `editorRootRef` instead, and each puts it on its outermost element.
 */
export const EDITOR_ROOT_REF_EXPORT_NAMES: ReadonlySet<string> = new Set([
  'Image',
]);

export const shouldRenderInline = (exportName: string): boolean =>
  INLINE_COMPONENT_EXPORT_NAMES.has(exportName);

export const takesEditorRootRef = (exportName: string): boolean =>
  EDITOR_ROOT_REF_EXPORT_NAMES.has(exportName);

/** The drag axis values Puck's slot render function accepts as `collisionAxis`. */
export type PuckSlotCollisionAxis = 'dynamic' | 'x' | 'y';

/**
 * Children slots whose items sit side by side even though Puck cannot tell
 * from the zone's own layout. Inside a table the zone is `display: contents`,
 * so Puck would pick the vertical axis for a row's cells.
 */
export const CHILDREN_SLOT_COLLISION_AXIS_BY_EXPORT_NAME: ReadonlyMap<
  string,
  PuckSlotCollisionAxis
> = new Map([['TableRow', 'x']]);

export const childrenSlotCollisionAxis = (
  exportName: string,
): PuckSlotCollisionAxis | undefined =>
  CHILDREN_SLOT_COLLISION_AXIS_BY_EXPORT_NAME.get(exportName);

/**
 * Slot children that register with {@link SlotHost} on the editor canvas when
 * Puck mounts opaque DropZoneChild wrappers (no `item` / empty closed-over
 * `content`). Keep in sync with children slots that set
 * `resolveSlotChildren` (`TabItem` under Tabs, `StatColumn` under
 * StatColumns). Collection lives in the adapter — not in those components.
 */
export const SLOT_COLLECTION_CHILD_EXPORT_NAMES: ReadonlySet<string> = new Set([
  'StatColumn',
  'TabItem',
]);

export const shouldCollectSlotChild = (exportName: string): boolean =>
  SLOT_COLLECTION_CHILD_EXPORT_NAMES.has(exportName);

type RegisteredSlotChild = {
  exportName: string;
  props: Record<string, unknown>;
};

type SlotCollectionApi = {
  /**
   * Returns a monotonic epoch for this `id`. Unregister must pass the same
   * epoch so a DropZone remount of a *new* collector instance cannot clear a
   * newer registration (per-instance generation refs cannot see across remounts).
   */
  register: (
    id: string,
    exportName: string,
    props: Record<string, unknown>,
  ) => number;
  unregister: (id: string, epoch: number) => void;
  /** Records the DOM element a child's panel slot is portaled into. */
  setPanelTarget: (id: string, element: HTMLElement | null) => void;
};

const SlotCollectionContext = createContext<SlotCollectionApi | null>(null);

type SlotPanelTargets = ReadonlyMap<string, HTMLElement>;

const NO_PANEL_TARGETS: SlotPanelTargets = new Map();

const SlotPanelTargetsContext =
  createContext<SlotPanelTargets>(NO_PANEL_TARGETS);

/**
 * Where a collected child's panel slot appears in the host. The slot itself
 * stays mounted inside the child's own Puck item, so Puck names its drop area
 * after that child; this element only receives its DOM through a portal.
 */
const SlotPanelTarget = ({ panelId }: { panelId: string }): ReactNode => {
  const collection = useContext(SlotCollectionContext);
  const setPanelTarget = collection?.setPanelTarget;
  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      setPanelTarget?.(panelId, element);
    },
    [setPanelTarget, panelId],
  );
  return createElement('div', {
    ref: attach,
    'data-slot-panel-target': panelId,
    style: { display: 'contents' },
  });
};

/**
 * Invokes the slot function from a component with a stable type, so a new
 * slot function identity updates the DropZone in place instead of remounting
 * it.
 */
const SlotPanelContent = ({
  render,
}: {
  render: PuckSlotRenderFn;
}): ReactNode => render({});

/**
 * Meta fields that may commit a registry update. `children` is intentionally
 * excluded: Puck/DropZone pass a new element or slot-function identity on
 * every parent re-render, and treating that as a change re-enters
 * `setRegistry` → HostAs render → new children identity forever (main-thread
 * freeze / Chrome "Page Unresponsive").
 */
const registrationPropsEqual = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean =>
  a.label === b.label && a.value === b.value && a.disabled === b.disabled;

const registrationIdFor = (
  props: Record<string, unknown>,
  fallbackId: string,
): string =>
  typeof props.id === 'string' && props.id.length > 0 ? props.id : fallbackId;

/**
 * Wraps a Puck `render` so DropZone-mounted slot children register with the
 * nearest {@link SlotHost}. No-op outside a SlotHost (runtime / Accordion).
 *
 * Prefer Puck's stable `props.id` as the registry key. `useId()` alone churns
 * when DropZone remounts collectors after a panel slot paints, which deletes
 * and re-adds the same child forever (`setRegistry` → main-thread freeze).
 *
 * `exportName` is the barrel name reconstructed on the empty-content editor
 * path. Callers that omit it keep the TabItem default.
 */
export const withSlotChildRegistration = (
  render: (props: Record<string, unknown>) => ReactNode,
  exportName = 'TabItem',
): ((props: Record<string, unknown>) => ReactNode) => {
  function SlotChildCollectable(props: Record<string, unknown>): ReactNode {
    const collection = useContext(SlotCollectionContext);
    const panelTargets = useContext(SlotPanelTargetsContext);
    const fallbackId = useId();
    const id = registrationIdFor(props, fallbackId);
    const propsRef = useRef(props);
    propsRef.current = props;

    useLayoutEffect(() => {
      if (!collection) return;
      // Read latest props (including children) at commit time, but only
      // depend on stable meta — see registrationPropsEqual.
      const epoch = collection.register(id, exportName, propsRef.current);
      return () => {
        // DropZone remounts collectors as new component instances. A
        // per-instance generation ref cannot see the remount's register, so
        // the HostAs-owned epoch gates unregister instead.
        queueMicrotask(() => {
          collection.unregister(id, epoch);
        });
      };
    }, [collection, id, props.label, props.value, props.disabled]);

    // Inside SlotHost the host reconstructs visible children from the
    // registry. Still invoke render outside a host (no-op for TabItem).
    if (collection) {
      // A panel slot function is mounted here, inside this item's Puck
      // context, so its drop area belongs to this item. The host only
      // supplies the element it appears in.
      const panelSlot = props.children;
      const target = panelTargets.get(id);
      if (typeof panelSlot === 'function' && target) {
        return createPortal(
          createElement(SlotPanelContent, {
            render: panelSlot as PuckSlotRenderFn,
          }),
          target,
        );
      }
      return null;
    }
    return render(props);
  }
  SlotChildCollectable.displayName = 'SlotChildCollectable';
  return SlotChildCollectable;
};

type PuckItemNode = {
  type: string;
  props?: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Puck config keys are barrel export names (`TabItem`). Stored nodes
 * sometimes still carry catalog ids (`hen.tabItem`). Flatten must resolve
 * both so nested `{ type, props }` reconstructs as barrel elements.
 */
export const puckTypeToExportName = (type: string): string => {
  if (!type.startsWith('hen.')) return type;
  const mapped = CATALOG_ID_TO_EXPORT_NAME[type];
  if (mapped) return mapped;
  const local = type.slice(4);
  if (!local) return type;
  return local.charAt(0).toUpperCase() + local.slice(1);
};

const resolveBarrelComponent = (
  type: string,
  resolveComponent: ResolveSlotComponent,
): ReturnType<ResolveSlotComponent> =>
  resolveComponent(type) ?? resolveComponent(puckTypeToExportName(type));

const isPuckItemNode = (value: unknown): value is PuckItemNode =>
  isRecord(value) && typeof value.type === 'string' && isRecord(value.props);

/**
 * Editor synthetic leaf from Admin `wrapInlineCompositionData` / DslConfig
 * `toSlotChild`. Not a hen-ui barrel export — reconstruct as the string
 * `props.text` so hen-ui Text keeps its copy.
 */
const isPuckTextType = (type: string): boolean =>
  type === 'PuckText' || puckTypeToExportName(type) === 'PuckText';

const extractPuckTextLeaf = (item: PuckItemNode): string => {
  const text = item.props?.text;
  return typeof text === 'string' ? text : '';
};

const hasPuckItemDescendant = (value: unknown): boolean => {
  if (isPuckItemNode(value)) return true;
  if (Array.isArray(value)) return value.some(hasPuckItemDescendant);
  return false;
};

const readPuckItem = (props: unknown): PuckItemNode | undefined => {
  if (!isRecord(props)) return undefined;
  const item = props.item;
  if (!isPuckItemNode(item)) return undefined;
  return item;
};

/**
 * Mount function-valued slot props so `Children.toArray` can walk them.
 * Skips React event handlers (`onX`) so reconstruct does not wrap callbacks.
 */
export const materializeFunctionSlots = (
  props: Record<string, unknown>,
): Record<string, unknown> => {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (key === 'puck' || key === 'editMode') continue;
    if (typeof value === 'function' && !/^on[A-Z]/.test(key)) {
      next[key] = createElement(
        value as ComponentType<Record<string, unknown>>,
      );
      continue;
    }
    next[key] = value;
  }
  return next;
};

const isFragmentElement = (
  child: ReactNode,
): child is ReactElement<{ children?: ReactNode }> =>
  isValidElement(child) && child.type === Fragment;

const unwrapSingleFragment = (children: ReactNode): ReactNode =>
  isFragmentElement(children) ? children.props.children : children;

const childKeyFor = (
  itemProps: Record<string, unknown>,
  fallback: string,
): string => (typeof itemProps.id === 'string' ? itemProps.id : fallback);

/**
 * Nested slot values are JSON (`{ type, props }` or arrays of those), not
 * React nodes. A one-element slot may be a single node instead of `[node]`.
 * Mixed arrays (puck nodes + string leaves) must still convert the nodes or
 * React throws "Objects are not valid as a React child".
 */
const reconstructSlotValue = (
  value: unknown,
  resolveComponent: ResolveSlotComponent,
  key: string,
): unknown => {
  if (Array.isArray(value)) {
    if (value.length === 0) return value;
    return value.map((entry, index) =>
      reconstructSlotValue(entry, resolveComponent, `${key}-${index}`),
    );
  }
  if (isPuckItemNode(value)) {
    const nestedKey = childKeyFor(value.props ?? {}, key);
    return reconstructPuckItem(value, resolveComponent, nestedKey) ?? null;
  }
  return value;
};

const reconstructPuckItem = (
  puckItem: PuckItemNode,
  resolveComponent: ResolveSlotComponent,
  key: string,
): ReactElement | string | null => {
  if (isPuckTextType(puckItem.type)) {
    return extractPuckTextLeaf(puckItem);
  }

  const Component = resolveBarrelComponent(puckItem.type, resolveComponent);
  if (!Component) return null;

  if (Component.ownsSlotProps === true) {
    return createElement(Component, { ...puckItem.props, key });
  }

  const itemProps = materializeFunctionSlots(puckItem.props ?? {});
  const nextProps: Record<string, unknown> = { ...itemProps };
  for (const [propName, value] of Object.entries(nextProps)) {
    nextProps[propName] = reconstructSlotValue(
      value,
      resolveComponent,
      `${key}-${propName}`,
    );
  }

  return createElement(Component, { ...nextProps, key });
};

/**
 * Turn Puck DropZone / SlotRender children (`{ item, config }`) into real
 * barrel component elements so a parent can `Children.toArray` them.
 * Already-real elements (Storybook, tests) pass through.
 *
 * Nested `{ type, props }` values (for example Text inside a TabItem panel)
 * are reconstructed recursively using barrel export names. Leaving those
 * objects on `item.props` would mount JSON as React children and render a
 * blank canvas.
 *
 * DropZone host wrappers (`<div data-puck-component>…</div>`) are unwrapped
 * so the parent sees the reconstructed (or already-real) child — not the
 * wrapper. Function components such as TabItem are not unwrapped, so panel
 * children stay on the TabItem.
 */
export const flattenPuckSlotChildren = (
  children: ReactNode,
  resolveComponent: ResolveSlotComponent,
): ReactNode => {
  const list = Children.toArray(unwrapSingleFragment(children));
  if (list.length === 0) return children ?? null;

  return list.flatMap((child, index) => {
    if (!isValidElement(child)) return [child];
    const puckItem = readPuckItem(child.props);
    if (puckItem) {
      const key = childKeyFor(puckItem.props ?? {}, String(child.key ?? index));
      return [reconstructPuckItem(puckItem, resolveComponent, key) ?? child];
    }

    if (typeof child.type === 'string') {
      const inner = (child.props as { children?: ReactNode }).children;
      if (inner != null) {
        const hoisted = flattenPuckSlotChildren(inner, resolveComponent);
        const hoistedList = Children.toArray(hoisted);
        if (hoistedList.length > 0) return hoistedList;
      }
    }

    const childProps = child.props as { children?: unknown };
    if (hasPuckItemDescendant(childProps.children)) {
      const nestedKey = childKeyFor(
        (isRecord(child.props) ? child.props : {}) as Record<string, unknown>,
        String(child.key ?? index),
      );
      return [
        cloneElement(
          child,
          undefined,
          reconstructSlotValue(
            childProps.children,
            resolveComponent,
            `${nestedKey}-children`,
          ) as ReactNode,
        ),
      ];
    }

    return [child];
  });
};

/**
 * Puck's slot transform closes over the raw zone `content` array
 * (`{ type, props }[]`). Invoking the slot function returns a DropZone /
 * SlotRender element that still carries that array on props.
 *
 * On the **editor** canvas (`@puckeditor/core` 0.21 DropZoneEdit path) that
 * closed-over array is empty: `flattenNode` → `stripSlots` nulls slot props in
 * `flatData`, and DropZoneEdit ignores `content` in favor of zone
 * `contentIds`. {@link SlotHost} then reconstructs host children from
 * {@link withSlotChildRegistration} instead. SlotRender / tests that still
 * put a non-empty `content` array on the element can use this probe.
 */
export const readSlotContentItems = (
  slot: PuckSlotFn,
): PuckItemNode[] | null => {
  if (typeof slot !== 'function') return null;
  try {
    const probed = (slot as PuckSlotRenderFn)({});
    if (!isValidElement(probed)) return null;
    const content = (probed.props as { content?: unknown }).content;
    if (!Array.isArray(content) || content.length === 0) return null;
    const items = content.filter(isPuckItemNode);
    return items.length > 0 ? items : null;
  } catch {
    return null;
  }
};

const slotContentToFlattenableChildren = (items: PuckItemNode[]): ReactNode =>
  items.map((item, index) =>
    createElement('div', {
      key: childKeyFor(item.props ?? {}, String(index)),
      item,
    }),
  );

const childrenHavePuckItems = (node: ReactNode): boolean => {
  for (const child of Children.toArray(unwrapSingleFragment(node))) {
    if (!isValidElement(child)) continue;
    if (readPuckItem(child.props)) return true;
    const nested = (child.props as { children?: unknown }).children;
    if (hasPuckItemDescendant(nested)) return true;
    if (nested != null && childrenHavePuckItems(nested as ReactNode)) {
      return true;
    }
  }
  return false;
};

const registeredPropsToHostChildren = (
  registry: Map<string, RegisteredSlotChild>,
  resolveComponent: ResolveSlotComponent,
): ReactNode => {
  const children = Array.from(registry.entries()).flatMap(([id, entry]) => {
    const Child = resolveComponent(entry.exportName);
    if (!Child) return [];
    const { props } = entry;
    const next = materializeFunctionSlots(props);
    // Panel `children` is often a Puck slot/DropZone function. Prefer a
    // non-empty closed-over `content` array (SlotRender / tests) so nested
    // Text reconstructs as barrel elements. On the editor canvas that array
    // is empty after stripSlots — leave a target element in the panel; the
    // registered child portals its own slot into it (see
    // withSlotChildRegistration), so Puck owns the drop area by that child.
    // Registration ignores children identity (see registrationPropsEqual).
    if (typeof props.children === 'function') {
      const items = readSlotContentItems(props.children as PuckSlotFn);
      next.children = items
        ? flattenPuckSlotChildren(
            slotContentToFlattenableChildren(items),
            resolveComponent,
          )
        : createElement(SlotPanelTarget, { panelId: id });
    }
    return [
      createElement(Child, {
        ...next,
        key: id,
      }),
    ];
  });
  return children.length > 0 ? children : null;
};

/**
 * Author/catalog empty strings are not a selected tab — treat them as unset
 * so canvas locking can seed from the first TabItem instead of locking Radix
 * to `value=""` (every trigger stays inactive).
 */
const usableTabsValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const slugFromTabLabel = (label: unknown, index: number): string => {
  if (typeof label === 'string' && label.trim() !== '') {
    return label.trim().toLowerCase().replace(/\s+/g, '-');
  }
  return `tab-${index}`;
};

/**
 * Match Tabs' first-item value resolution so canvas locking selects a real
 * trigger (`tab-1` from label "Tab 1") when authors omit `value`.
 */
const firstTabValueFromChildren = (node: ReactNode): string | undefined => {
  let index = 0;
  for (const child of Children.toArray(node)) {
    if (!isValidElement(child)) continue;
    const props = child.props as { value?: unknown; label?: unknown };
    const explicit = usableTabsValue(props.value);
    if (explicit) return explicit;
    if (typeof props.label === 'string' || props.label == null) {
      // Only derive from label for TabItem-shaped children (have label/value).
      if ('label' in props || 'value' in props) {
        return slugFromTabLabel(props.label, index);
      }
    }
    index += 1;
  }
  return undefined;
};

/**
 * The Open modal `tab`, when one of these TabItems has it as its value.
 * Matches Tabs, which also derives a value from the label when it is blank.
 */
const requestedTabFromChildren = (
  payload: ModalOpenPayload | undefined,
  node: ReactNode,
): string | undefined => {
  const requested = usableTabsValue(payload?.tab);
  if (requested === undefined) return undefined;
  let index = 0;
  for (const child of Children.toArray(node)) {
    if (!isValidElement(child)) continue;
    const props = child.props as { value?: unknown; label?: unknown };
    if (!('label' in props || 'value' in props)) continue;
    const value =
      usableTabsValue(props.value) ?? slugFromTabLabel(props.label, index);
    if (value === requested) return requested;
    index += 1;
  }
  return undefined;
};

/**
 * Canvas SlotHost mounts Tabs with empty children first, then reconstructs
 * TabItems after registration. Tabs derives Radix `value` as
 * `uncontrolledValue ?? firstValue` and only spreads it when non-null — so
 * the first paint omits `value` (uncontrolled) and the next adds it
 * (controlled), which freezes the editor tab. Force a string `value` for
 * the lifetime of the SlotHost mount when the author did not already
 * control it. Preview/runtime never hit this path (no SlotHost).
 */
const withLockedTabsValue = (
  hostProps: Record<string, unknown>,
  canvasValue: string,
  setCanvasValue: (next: string) => void,
): Record<string, unknown> => {
  if (usableTabsValue(hostProps.value) !== undefined) {
    return hostProps;
  }

  const authorOnChange = hostProps.onValueChange;
  return {
    ...hostProps,
    defaultValue: undefined,
    // Prefer a seeded canvas value; fall back to a non-empty authored
    // defaultValue so the first paint is already controlled.
    value:
      usableTabsValue(canvasValue) ??
      usableTabsValue(hostProps.defaultValue) ??
      '',
    onValueChange: (next: string) => {
      setCanvasValue(next);
      if (typeof authorOnChange === 'function') {
        (authorOnChange as (value: string) => void)(next);
      }
    },
  };
};

export interface SlotHostProps {
  Host: ComponentType<Record<string, unknown>>;
  hostProps: Record<string, unknown>;
  slot: PuckSlotFn;
  resolveComponent: ResolveSlotComponent;
  /**
   * `element` — `createElement(slot, { as })` for hooky editor DropZones.
   * `render` — invoke `slot({ as })` like DslConfig StableSlot (stable type,
   * changing function identity as a prop).
   */
  mode?: 'element' | 'render';
}

/**
 * Mount a Puck slot so the host receives walkable child elements.
 *
 * Puck's SlotRender calls `as` with mapped items. Using a stable `as` that
 * renders `Host` inverts the tree: the host sees reconstructed barrel
 * elements instead of the SlotFn itself. Accordion stays on the default
 * mount-SlotFn-as-children path; this host is opt-in via
 * `parameters.edu.resolveSlotChildren`.
 *
 * On the editor canvas, DropZone mounts opaque `DropZoneChild` wrappers as
 * `as` children (no `item` prop; closed-over `content` is empty after
 * stripSlots). Prefer non-empty slot `content` when present; otherwise mount
 * those wrappers so {@link withSlotChildRegistration} can register props and
 * reconstruct real barrel elements for the unchanged host (Tabs).
 */
export const SlotHost = ({
  Host,
  hostProps,
  slot,
  resolveComponent,
  mode = 'element',
}: SlotHostProps) => {
  const hostPropsRef = useRef(hostProps);
  hostPropsRef.current = hostProps;
  const HostRef = useRef(Host);
  HostRef.current = Host;
  const resolveRef = useRef(resolveComponent);
  resolveRef.current = resolveComponent;

  const slotContentRef = useRef<ReactNode>(null);
  const contentItems = readSlotContentItems(slot);
  slotContentRef.current = contentItems
    ? slotContentToFlattenableChildren(contentItems)
    : null;

  const HostAs = useMemo(
    () =>
      forwardRef<unknown, { children?: ReactNode }>(function SlotHostAs(
        { children },
        _ref,
      ) {
        const [registry, setRegistry] = useState(
          () => new Map<string, RegisteredSlotChild>(),
        );
        const [canvasTabsValue, setCanvasTabsValue] = useState(() => {
          const initial = hostPropsRef.current;
          return (
            usableTabsValue(initial.value) ??
            usableTabsValue(initial.defaultValue) ??
            ''
          );
        });

        const [panelTargets, setPanelTargets] =
          useState<SlotPanelTargets>(NO_PANEL_TARGETS);

        // The tab value is locked here, so a changed authored default tab
        // (edited on the canvas) has to be applied to the locked value.
        const authoredDefaultTab = usableTabsValue(
          hostPropsRef.current.defaultValue,
        );
        const [appliedDefaultTab, setAppliedDefaultTab] =
          useState(authoredDefaultTab);
        if (authoredDefaultTab !== appliedDefaultTab) {
          setAppliedDefaultTab(authoredDefaultTab);
          // Cleared: fall back to the first tab, as Preview does.
          setCanvasTabsValue(authoredDefaultTab ?? '');
        }

        const registrationEpochByIdRef = useRef(new Map<string, number>());
        const collectionApi = useMemo<SlotCollectionApi>(
          () => ({
            register: (id, childExportName, props) => {
              const epoch = (registrationEpochByIdRef.current.get(id) ?? 0) + 1;
              registrationEpochByIdRef.current.set(id, epoch);
              setRegistry((prev) => {
                const existing = prev.get(id);
                if (
                  existing &&
                  existing.exportName === childExportName &&
                  registrationPropsEqual(existing.props, props)
                ) {
                  // Keep the latest panel slot fn without notifying React —
                  // children identity churn must not re-enter setRegistry.
                  // Never mutate `existing` in place: React freezes props in
                  // development, and the first register may have stored that
                  // frozen object (`Cannot assign to read only property
                  // 'children'` → canvas error boundary / "Try again").
                  prev.set(id, {
                    exportName: childExportName,
                    props: { ...existing.props, children: props.children },
                  });
                  return prev;
                }
                const next = new Map(prev);
                // Always store a mutable shallow copy — never the frozen
                // React props object itself.
                next.set(id, {
                  exportName: childExportName,
                  props: { ...props },
                });
                return next;
              });
              return epoch;
            },
            unregister: (id, epoch) => {
              if (registrationEpochByIdRef.current.get(id) !== epoch) {
                return;
              }
              registrationEpochByIdRef.current.delete(id);
              setRegistry((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Map(prev);
                next.delete(id);
                return next;
              });
            },
            setPanelTarget: (id, element) => {
              setPanelTargets((prev) => {
                if ((prev.get(id) ?? null) === element) return prev;
                const next = new Map(prev);
                if (element) next.set(id, element);
                else next.delete(id);
                return next;
              });
            },
          }),
          [],
        );

        const fromContent = slotContentRef.current;
        let hostChildren: ReactNode;
        if (fromContent) {
          hostChildren = flattenPuckSlotChildren(
            fromContent,
            resolveRef.current,
          );
        } else if (registry.size > 0) {
          hostChildren = registeredPropsToHostChildren(
            registry,
            resolveRef.current,
          );
        } else if (childrenHavePuckItems(children)) {
          // SlotRender / tests: `{ item }` nodes are safe to flatten into the
          // host. Opaque editor DropZoneChildren have no `item` — leave the
          // host empty until registration commits (avoids doubling zones).
          hostChildren = flattenPuckSlotChildren(children, resolveRef.current);
        } else {
          hostChildren = null;
        }

        // The tab value is locked here, so Tabs cannot apply the Open modal
        // tab itself. Each open hands over a new payload; TabItems can
        // register after it arrives, so apply it once the tab exists.
        const openPayload = useModalOpenPayload();
        const requestedTab = requestedTabFromChildren(
          openPayload,
          hostChildren,
        );
        const [appliedPayload, setAppliedPayload] =
          useState<ModalOpenPayload>();
        if (requestedTab !== undefined && appliedPayload !== openPayload) {
          setAppliedPayload(openPayload);
          setCanvasTabsValue(requestedTab);
        }

        useLayoutEffect(() => {
          if (usableTabsValue(hostPropsRef.current.value) !== undefined) return;
          if (usableTabsValue(canvasTabsValue) !== undefined) return;
          const firstChildValue = firstTabValueFromChildren(hostChildren);
          if (firstChildValue) setCanvasTabsValue(firstChildValue);
        }, [hostChildren, canvasTabsValue]);

        const lockedHostProps = withLockedTabsValue(
          hostPropsRef.current,
          canvasTabsValue,
          setCanvasTabsValue,
        );

        return createElement(
          SlotCollectionContext.Provider,
          { value: collectionApi },
          createElement(
            SlotPanelTargetsContext.Provider,
            { value: panelTargets },
            createElement(
              Fragment,
              null,
              !fromContent && children != null
                ? createElement(
                    'div',
                    { hidden: true, 'aria-hidden': true },
                    children,
                  )
                : null,
              createElement(HostRef.current, {
                ...lockedHostProps,
                children: hostChildren,
              }),
            ),
          ),
        );
      }),
    [],
  );

  if (mode === 'render') {
    return (slot as PuckSlotRenderFn)({ as: HostAs });
  }

  return createElement(slot as ComponentType<{ as?: PuckSlotAs }>, {
    as: HostAs,
  });
};

SlotHost.displayName = 'SlotHost';
